import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { askChat } from '../api/rag'
import { isRateLimited } from '../api/errors'
import { DEFAULT_LANGUAGE, isSupportedLanguage } from '../i18n'

interface Message {
  id: number
  role: 'user' | 'assistant'
  text: string
}

type Status = 'idle' | 'sending' | 'error' | 'rate-limited'

/**
 * Floating RAG chat assistant, shown on every public page via LangLayout.
 *
 * - No network request on mount: the first call to /rag/ask only happens when
 *   the visitor actually sends a question.
 * - History lives in React state for the session only. No localStorage, no
 *   persistence, no analytics. A page refresh clears it, by design.
 * - Transparency (EU AI Act): the header carries an "AI Assistant" label and
 *   the opening message says it is an AI assistant. No human name or photo.
 */
function ChatWidget() {
  const { t } = useTranslation()
  // The site language is driven by the URL (/:lang/...), so the current
  // route param is the authoritative "active language" and it is always
  // read fresh on each render, never captured when the widget mounts.
  const { lang } = useParams()

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [teaserVisible, setTeaserVisible] = useState(false)

  const launcherRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const logRef = useRef<HTMLDivElement>(null)
  const hasOpenedRef = useRef(false)
  const nextId = useRef(1)

  const makeId = () => nextId.current++

  function openPanel() {
    setTeaserVisible(false)
    setMessages((prev) =>
      prev.length > 0
        ? prev
        : [{ id: makeId(), role: 'assistant', text: t('chat.greeting') }],
    )
    setOpen(true)
  }

  function closePanel() {
    setOpen(false)
  }

  // Proactive greeting bubble: appears once, a few seconds after the page
  // loads, next to the still-closed launcher - unless the visitor has
  // already opened the chat themselves by then. Runs once per mount (this
  // component lives for the whole browsing session, not per page - see
  // LangLayout), so "once per session" falls out of that rather than
  // needing sessionStorage.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!hasOpenedRef.current) {
        setTeaserVisible(true)
      }
    }, 6000)
    return () => window.clearTimeout(timer)
  }, [])

  // Focus into the panel on open; hand focus back to the launcher on close
  // (only once the panel has actually been opened, so nothing is stolen on
  // the first render).
  useEffect(() => {
    if (open) {
      hasOpenedRef.current = true
      inputRef.current?.focus()
    } else if (hasOpenedRef.current) {
      launcherRef.current?.focus()
    }
  }, [open])

  // Keep the newest message in view.
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, status])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const question = input.trim()
    if (!question || status === 'sending') return

    setMessages((prev) => [
      ...prev,
      { id: makeId(), role: 'user', text: question },
    ])
    setInput('')
    setStatus('sending')

    // Resolve the language at send time, from the live URL param, so a
    // language switch while the chat is open takes effect on the very next
    // question. Every new answer follows the current site language.
    const language =
      lang && isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE

    try {
      const answer = await askChat(question, language)
      setMessages((prev) => [
        ...prev,
        { id: makeId(), role: 'assistant', text: answer },
      ])
      setStatus('idle')
    } catch (err) {
      setStatus(isRateLimited(err) ? 'rate-limited' : 'error')
    }
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      closePanel()
    }
  }

  const title = `${t('chat.headerName')} ${t('chat.aiLabel')}`

  return (
    <>
      {!open && teaserVisible && (
        <div className="chat-teaser" role="status">
          <button
            type="button"
            className="chat-teaser-body"
            onClick={openPanel}
          >
            <span className="chat-teaser-label">{t('chat.aiLabel')}</span>
            <span className="chat-teaser-text">{t('chat.teaserText')}</span>
          </button>
          <button
            type="button"
            className="chat-teaser-dismiss"
            aria-label={t('chat.teaserDismiss')}
            onClick={() => setTeaserVisible(false)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M6 6l12 12M18 6L6 18"
              />
            </svg>
          </button>
        </div>
      )}

      {!open && (
        <button
          ref={launcherRef}
          type="button"
          className="chat-launcher"
          aria-label={t('chat.open')}
          aria-haspopup="dialog"
          aria-expanded={false}
          onClick={openPanel}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
              d="M4 5h16v11H8l-4 4z"
            />
          </svg>
        </button>
      )}

      {open && (
        <div
          className="chat-panel"
          role="dialog"
          aria-label={title}
          onKeyDown={handleKeyDown}
        >
          <div className="chat-header">
            <span className="chat-header-name">{t('chat.headerName')}</span>
            <span className="chat-ai-label">{t('chat.aiLabel')}</span>
            <button
              type="button"
              className="chat-close"
              aria-label={t('chat.close')}
              onClick={closePanel}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  d="M6 6l12 12M18 6L6 18"
                />
              </svg>
            </button>
          </div>

          <div
            ref={logRef}
            className="chat-log"
            role="log"
            aria-live="polite"
            aria-atomic="false"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-msg chat-msg--${message.role}`}
              >
                <span className="chat-msg-role">
                  {message.role === 'user'
                    ? t('chat.youLabel')
                    : t('chat.assistantLabel')}
                </span>
                {message.text}
              </div>
            ))}
            {status === 'sending' && (
              <p className="chat-typing">{t('chat.sending')}</p>
            )}
          </div>

          {(status === 'error' || status === 'rate-limited') && (
            <p className="chat-error" role="alert">
              {status === 'rate-limited'
                ? t('chat.errorRateLimited')
                : t('chat.errorGeneric')}
            </p>
          )}

          <form className="chat-form" onSubmit={handleSubmit}>
            <label className="visually-hidden" htmlFor="chat-input">
              {t('chat.inputPlaceholder')}
            </label>
            <input
              id="chat-input"
              ref={inputRef}
              className="chat-input"
              type="text"
              autoComplete="off"
              value={input}
              placeholder={t('chat.inputPlaceholder')}
              onChange={(event) => setInput(event.target.value)}
            />
            <button
              type="submit"
              className="btn btn--primary chat-send"
              aria-label={t('chat.send')}
              disabled={status === 'sending' || input.trim() === ''}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path fill="currentColor" d="M3 11l17-8-8 17-2-7-7-2z" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  )
}

export default ChatWidget
